import { z } from "zod";
/**
 * Distance is always expressed with an explicit unit, mirroring Mass/
 * Temperature (`COMPANY.md` "Target Market": "distances/times carry an
 * explicit unit in the data model"). Metric (meters) is the only unit
 * exposed in the UI at launch; Furlongs is the standard Imperial distance
 * unit in horse racing, modeled so a later locale can enable it without a
 * data migration.
 */
export const DISTANCE_UNITS = ["METERS", "FURLONGS"];
export const DistanceUnitSchema = z.enum(DISTANCE_UNITS);
export const TRAINING_DISTANCE_VALUE_MIN = 0;
export const TRAINING_DISTANCE_VALUE_MAX = 100_000;
/** Durations are stored as integer milliseconds (`packages/database/
 * AGENTS.md`: "durations as integer milliseconds"), capped at 24 hours —
 * generous for any single training session. */
export const TRAINING_DURATION_MILLISECONDS_MAX = 86_400_000;
export const WORK_TYPE_MAX_LENGTH = 120;
export const TRAINING_INSTRUCTIONS_MAX_LENGTH = 2000;
export const TRAINING_OUTCOME_NOTES_MAX_LENGTH = 2000;
/**
 * A Training Scheduler entry is "completed" (open to logging outcome notes,
 * `docs/products/stable.md`: "Logging notes on a completed entry") once its
 * scheduled day has arrived — a future-dated (planned) entry has nothing to
 * log yet. Both dates are local calendar dates (`YYYY-MM-DD`), which sort
 * correctly as plain strings.
 */
export function canLogTrainingOutcome(entryDay, today) {
    return entryDay <= today;
}
/** The calendar day immediately before `day` (`YYYY-MM-DD`), used to look up
 * a horse's prior day's entry for cell-placement prefill. Computed in UTC so
 * the result never depends on the server's local timezone. */
export function previousCalendarDay(day) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (match === null) {
        throw new Error(`previousCalendarDay expects YYYY-MM-DD, got: ${day}`);
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const date = Number(match[3]);
    const instant = new Date(Date.UTC(year, month - 1, date));
    instant.setUTCDate(instant.getUTCDate() - 1);
    return formatCalendarDayUTC(instant);
}
/** A `Date` reduced to its UTC calendar day (`YYYY-MM-DD`) — the "today"
 * used by {@link canLogTrainingOutcome} when comparing against the wall
 * clock. */
export function toCalendarDayUTC(instant) {
    return formatCalendarDayUTC(instant);
}
function formatCalendarDayUTC(instant) {
    const y = String(instant.getUTCFullYear()).padStart(4, "0");
    const m = String(instant.getUTCMonth() + 1).padStart(2, "0");
    const d = String(instant.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
